class ElProductForm extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.activeSubmitButton = null;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.submitButtons = this.querySelectorAll('[type="submit"]');
    this.errorBox = this.querySelector("[data-error]");
    this.inputOptions = this.querySelectorAll(
      'input[name="subscription_option"]',
    );
    this.atcPrice = this.querySelector(".display__price");
    this.atcCompare = this.querySelector(".display__compare-price");

    if (!this.form) return;

    this.submitButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.activeSubmitButton = button;
      });
    });

    this.form.addEventListener("submit", this.onSubmit);
    this.initEvents();
    this.updateDisplayPrices();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.onSubmit);
    }

    if (this.inputOptions?.length) {
      this.inputOptions.forEach((input) => {
        input.removeEventListener("change", this.onOptionChange);
      });
    }
  }

  get variantId() {
    return this.form?.querySelector('input[name="id"]')?.value || null;
  }

  get selectedOption() {
    return (
      this.form?.querySelector('input[name="subscription_option"]:checked') ||
      null
    );
  }

  get selectedPrice() {
    return this.selectedOption?.dataset?.price || null;
  }

  get selectedComparePrice() {
    return this.selectedOption?.dataset?.comparePrice || null;
  }

  get selectedSellingPlanId() {
    return this.selectedOption?.dataset?.sellingPlanId || null;
  }

  get selectedQuantity() {
    return parseInt(this.selectedOption?.dataset?.quantity || "1", 10);
  }

  get purchaseType() {
    return this.activeSubmitButton?.value || "subscription";
  }

  openCartDrawer() {
    const cartDrawer =
      document.querySelector("cart-drawer-component") ||
      document.querySelector("cart-drawer");

    if (!cartDrawer) return;

    // Common Horizon/web-component patterns
    if (typeof cartDrawer.open === "function") {
      cartDrawer.open();
      return;
    }

    // Some versions use a boolean property
    if ("open" in cartDrawer) {
      cartDrawer.open = true;
    }

    // Safe fallbacks
    cartDrawer.setAttribute("open", "");
    cartDrawer.setAttribute("aria-hidden", "false");
  }

  setLoadingState(isLoading) {
    if (!this.submitButtons.length) return;

    this.submitButtons.forEach((button) => {
      button.disabled = isLoading;
      button.setAttribute("aria-busy", isLoading ? "true" : "false");
    });
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.hidden = false;
    this.errorBox.textContent = message;
  }

  clearError() {
    if (!this.errorBox) return;
    this.errorBox.hidden = true;
    this.errorBox.textContent = "";
  }

  displayPrice() {
    if (this.atcPrice) {
      this.atcPrice.textContent = this.selectedPrice || "";
    }
  }

  displayComparePrice() {
    if (this.atcCompare) {
      this.atcCompare.textContent = this.selectedComparePrice || "";
    }
  }

  updateDisplayPrices() {
    this.displayPrice();
    this.displayComparePrice();
  }

  onOptionChange() {
    this.updateDisplayPrices();
  }

  initEvents() {
    if (this.inputOptions?.length) {
      this.inputOptions.forEach((input) => {
        input.addEventListener("change", this.onOptionChange);
      });
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    this.clearError();

    const variantId = this.variantId;
    const purchaseType = this.purchaseType;

    if (!variantId) {
      this.showError("Variant ID not found.");
      return;
    }

    let payload = {
      id: variantId,
      quantity: 1,
    };

    if (purchaseType === "subscription") {
      const sellingPlanId = this.selectedSellingPlanId;
      const quantity = this.selectedQuantity;

      if (!sellingPlanId) {
        this.showError("Please select a subscription option.");
        return;
      }

      payload = {
        id: variantId,
        quantity,
        selling_plan: sellingPlanId,
      };
    }

    this.setLoadingState(true);

    try {
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const addData = await addResponse.json();

      if (!addResponse.ok) {
        this.showError(
          addData.description ||
            addData.message ||
            "Unable to add this item to cart.",
        );
        return;
      }

      const cartResponse = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });

      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              itemCount: cart.item_count,
              source: "product-form-component",
            },
          },
        }),
      );

      // let Horizon refresh first, then open drawer
      requestAnimationFrame(() => {
        this.openCartDrawer();
      });
    } catch (error) {
      console.error(error);
      this.showError("Something went wrong while adding to cart.");
    } finally {
      this.setLoadingState(false);
      this.activeSubmitButton = null;
    }
  }
}

if (!customElements.get("el-product-form")) {
  customElements.define("el-product-form", ElProductForm);
}
